import { useEffect } from "react";
import { BRAND_OG_IMAGE, SITE_NAME, SITE_URL, TWITTER_HANDLE } from "@/constants/site";
import {
  applyJsonLd,
  type JsonLdArticle,
  type JsonLdBreadcrumb,
  type JsonLdFaq,
  type JsonLdPlan,
} from "@/seo/jsonLd";

export { SITE_URL, SITE_NAME, TWITTER_HANDLE } from "@/constants/site";

const DEFAULT_IMAGE = BRAND_OG_IMAGE;

interface SeoProps {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  image?: string;
  /** Título traduzido para inglês/espanhol (usados em metadados de compartilhamento). */
  titleEn?: string;
  titleEs?: string;
  descriptionEn?: string;
  descriptionEs?: string;
  /** Perguntas frequentes visíveis na página (gera dados estruturados FAQPage). */
  faqs?: JsonLdFaq[];
  /** Planos visíveis na página (gera dados estruturados de produto com preço). */
  plans?: JsonLdPlan[];
  /** Migalhas de navegação visíveis na página (gera dados estruturados BreadcrumbList). */
  breadcrumbs?: JsonLdBreadcrumb[];
  /** Impede a indexação da página (usa robots noindex, follow e omite os alternates de idioma). */
  noindex?: boolean;
  /** Dados do artigo do blog (gera dados estruturados BlogPosting e metadados article). */
  article?: JsonLdArticle;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function withLang(url: string, lang: "en" | "es") {
  return url.includes("?") ? `${url}&lang=${lang}` : `${url}?lang=${lang}`;
}

/**
 * Gerenciador central de head/SEO por rota para a SPA. Atualiza title, description,
 * keywords, canonical, alternates hreflang (pt-BR / en / es) e compartilhamento
 * (Open Graph / Twitter) de forma idempotente a cada mudança de rota. Os dados
 * estruturados (JSON-LD) são gerados em um módulo separado (`src/seo/jsonLd`).
 */
export default function Seo({
  title,
  description,
  keywords,
  path = "/",
  image = DEFAULT_IMAGE,
  titleEn,
  titleEs,
  descriptionEn,
  descriptionEs,
  faqs,
  plans,
  breadcrumbs,
  article,
  noindex,
}: SeoProps) {
  useEffect(() => {
    const url = `${SITE_URL}${path === "/" ? "/" : path}`;
    const enUrl = withLang(url, "en");
    const esUrl = withLang(url, "es");

    document.documentElement.lang = "pt-BR";
    document.title = title;

    upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");

    upsertMeta("name", "description", description);
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertLink("canonical", url);

    // Alternates de idioma (pt-BR principal, en e es)
    if (!noindex) {
      upsertLink("alternate", url, "pt-BR");
      upsertLink("alternate", enUrl, "en");
      upsertLink("alternate", esUrl, "es");
      upsertLink("alternate", url, "x-default");
    }

    upsertMeta("property", "og:type", article ? "article" : "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "pt_BR");
    upsertMeta("property", "og:locale:alternate", "en_US");
    upsertMeta("property", "og:locale:alternate", "es_ES");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", `${SITE_NAME} — infraestrutura digital`);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:site", TWITTER_HANDLE);
    upsertMeta("name", "twitter:creator", TWITTER_HANDLE);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    if (article) {
      upsertMeta("property", "article:published_time", article.datePublished);
      upsertMeta("property", "article:modified_time", article.dateModified);
      upsertMeta("property", "article:section", article.section);
      upsertMeta("property", "article:author", article.author);
    }

    // Dados estruturados (JSON-LD) — módulo isolado da mutação de meta tags de sharing.
    applyJsonLd({
      title,
      description,
      url,
      image,
      titleEn,
      titleEs,
      descriptionEn,
      descriptionEs,
      faqs,
      plans,
      breadcrumbs,
      article,
    });
  }, [
    title,
    description,
    keywords,
    path,
    image,
    titleEn,
    titleEs,
    descriptionEn,
    descriptionEs,
    faqs,
    plans,
    breadcrumbs,
    article,
    noindex,
  ]);

  return null;
}